import React, { useState, useEffect } from 'react'
import Modal from './Modal'
import LeadDocuments from './LeadDocuments'
import LeadFollowUp from './LeadFollowUp'
import QuickIntegrations from './QuickIntegrations'
import api from '../services/api'

interface Interaction {
  id: number
  lead: number
  lead_name: string
  interaction_type: string
  interaction_type_display: string
  subject: string | null
  notes: string
  direction: string
  duration_minutes: number | null
  created_by: number
  created_by_name: string
  has_attachments: boolean
  created_at: string
}

interface LeadInteractionsProps {
  leadId: number
  leadName: string
  followUpDate: string | null
  leadPhone?: string | null
  leadEmail?: string | null
  onClose: () => void
}

const LeadInteractions: React.FC<LeadInteractionsProps> = ({ 
  leadId, 
  leadName, 
  followUpDate,
  leadPhone,
  leadEmail,
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState<'interactions' | 'documents' | 'followup' | 'integrations'>('interactions')
  const [interactions, setInteractions] = useState<Interaction[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [filterType, setFilterType] = useState('all')
  const [newInteraction, setNewInteraction] = useState({
    interaction_type: 'call',
    subject: '',
    notes: '',
    direction: 'outbound',
    duration_minutes: ''
  })

  useEffect(() => {
    loadInteractions()
  }, [leadId])

  const loadInteractions = async () => {
    try {
      setIsLoading(true)
      const res = await api.get(`/leads/interactions/?lead=${leadId}`)
      const data = res.data
      setInteractions(Array.isArray(data) ? data : (data.results || []))
    } catch (err: any) {
      console.error('Error loading interactions:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateInteraction = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newInteraction.notes.trim()) return

    try {
      setIsCreating(true)
      await api.post('/leads/interactions/', {
        ...newInteraction,
        lead: leadId,
        duration_minutes: newInteraction.duration_minutes ? parseInt(newInteraction.duration_minutes) : null
      })

      setNewInteraction({
        interaction_type: 'call',
        subject: '',
        notes: '',
        direction: 'outbound',
        duration_minutes: ''
      })
      setShowCreateForm(false)
      loadInteractions()
    } catch (err: any) {
      console.error('Error creating interaction:', err)
      alert('Erro ao criar interação: ' + (err.message || 'Erro desconhecido'))
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeleteInteraction = async (interactionId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar esta interação?')) {
      return
    }

    try {
      await api.delete(`/leads/interactions/${interactionId}/`)
      loadInteractions()
    } catch (err: any) {
      console.error('Error deleting interaction:', err)
      alert('Erro ao deletar interação: ' + (err.message || 'Erro desconhecido'))
    }
  }

  const getInteractionIcon = (type: string) => {
    switch (type) {
      case 'call': return '📞'
      case 'email': return '📧'
      case 'whatsapp': return '💬'
      case 'meeting': return '🤝'
      case 'note': return '📝'
      case 'task': return '✅'
      case 'message': return '💬'
      default: return '📌'
    }
  }

  const getInteractionColor = (type: string) => {
    switch (type) {
      case 'call': return '#1976d2'
      case 'email': return '#ff9800'
      case 'whatsapp': return '#25d366'
      case 'meeting': return '#9c27b0'
      case 'note': return '#607d8b'
      case 'task': return '#4caf50'
      case 'message': return '#00bcd4'
      default: return '#9e9e9e'
    }
  }

  const filteredInteractions = filterType === 'all' 
    ? interactions 
    : interactions.filter(i => i.interaction_type === filterType)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return ''
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '70vh' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '16px' }}>
        <h3 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
          Histórico do Lead
        </h3>
        <p style={{ fontSize: '14px', color: '#666' }}>
          Lead: <strong>{leadName}</strong>
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #e0e0e0' }}>
        <button
          onClick={() => setActiveTab('interactions')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'interactions' ? '#1976d2' : 'transparent',
            color: activeTab === 'interactions' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'interactions' ? '3px solid #1976d2' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'interactions' ? '600' : '400',
            transition: 'all 0.2s'
          }}
        >
          📋 Interações
        </button>
        <button
          onClick={() => setActiveTab('followup')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'followup' ? '#1976d2' : 'transparent',
            color: activeTab === 'followup' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'followup' ? '3px solid #1976d2' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'followup' ? '600' : '400',
            transition: 'all 0.2s'
          }}
        >
          ⏰ Follow-up
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'integrations' ? '#1976d2' : 'transparent',
            color: activeTab === 'integrations' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'integrations' ? '3px solid #1976d2' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'integrations' ? '600' : '400',
            transition: 'all 0.2s'
          }}
        >
          🚀 Integrações
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'documents' ? '#1976d2' : 'transparent',
            color: activeTab === 'documents' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'documents' ? '3px solid #1976d2' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'documents' ? '600' : '400',
            transition: 'all 0.2s'
          }}
        >
          📎 Documentos
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'interactions' ? (
        <div>
          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              + Nova Interação
            </button>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                padding: '8px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">Todos os tipos</option>
              <option value="call">Ligações</option>
              <option value="email">Emails</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="meeting">Reuniões</option>
              <option value="note">Notas</option>
              <option value="task">Tarefas</option>
            </select>

            <span style={{ fontSize: '14px', color: '#666', marginLeft: 'auto' }}>
              {filteredInteractions.length} interações
            </span>
          </div>

          {/* Create Form */}
          {showCreateForm && (
            <form onSubmit={handleCreateInteraction} style={{
              padding: '16px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>
                  Tipo de Interação *
                </label>
                <select
                  value={newInteraction.interaction_type}
                  onChange={(e) => setNewInteraction({ ...newInteraction, interaction_type: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="call">📞 Ligação</option>
                  <option value="email">📧 Email</option>
                  <option value="whatsapp">💬 WhatsApp</option>
                  <option value="meeting">🤝 Reunião</option>
                  <option value="note">📝 Nota</option>
                  <option value="task">✅ Tarefa</option>
                  <option value="message">💬 Mensagem</option>
                  <option value="other">📌 Outro</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>
                  Assunto
                </label>
                <input
                  type="text"
                  value={newInteraction.subject}
                  onChange={(e) => setNewInteraction({ ...newInteraction, subject: e.target.value })}
                  placeholder="Assunto da interação..."
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>
                  Notas *
                </label>
                <textarea
                  value={newInteraction.notes}
                  onChange={(e) => setNewInteraction({ ...newInteraction, notes: e.target.value })}
                  required
                  placeholder="Descreva a interação..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>
                    Direção
                  </label>
                  <select
                    value={newInteraction.direction}
                    onChange={(e) => setNewInteraction({ ...newInteraction, direction: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="outbound">Saída (eu contatei)</option>
                    <option value="inbound">Entrada (me contataram)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>
                    Duração (minutos)
                  </label>
                  <input
                    type="number"
                    value={newInteraction.duration_minutes}
                    onChange={(e) => setNewInteraction({ ...newInteraction, duration_minutes: e.target.value })}
                    placeholder="Ex: 30"
                    min="0"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    fontSize: '14px'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isCreating ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: isCreating ? 0.5 : 1
                  }}
                >
                  {isCreating ? 'Salvando...' : 'Salvar Interação'}
                </button>
              </div>
            </form>
          )}

          {/* Interactions List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                <p style={{ color: '#666' }}>Carregando interações...</p>
              </div>
            ) : filteredInteractions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#999' }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>📋</div>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                  Nenhuma interação encontrada
                </p>
                <p style={{ fontSize: '14px' }}>
                  {showCreateForm ? 'Preencha o formulário acima' : 'Clique em "+ Nova Interação" para começar'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {filteredInteractions.map((interaction) => (
                  <div
                    key={interaction.id}
                    style={{
                      padding: '16px',
                      backgroundColor: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: getInteractionColor(interaction.interaction_type),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          flexShrink: 0
                        }}
                      >
                        {getInteractionIcon(interaction.interaction_type)}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                          <div>
                            <span style={{
                              fontWeight: '600',
                              fontSize: '14px',
                              color: '#333'
                            }}>
                              {interaction.interaction_type_display}
                            </span>
                            {interaction.subject && (
                              <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                                - {interaction.subject}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: interaction.direction === 'inbound' ? '#e3f2fd' : '#f5f5f5',
                              color: interaction.direction === 'inbound' ? '#1976d2' : '#616161'
                            }}>
                              {interaction.direction === 'inbound' ? '↙ Entrada' : '↗ Saída'}
                            </span>
                            {formatDuration(interaction.duration_minutes) && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                backgroundColor: '#fff3e0',
                                color: '#e65100'
                              }}>
                                {formatDuration(interaction.duration_minutes)}
                              </span>
                            )}
                            <button
                              onClick={() => handleDeleteInteraction(interaction.id)}
                              style={{
                                padding: '2px 6px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                            >
                              Deletar
                            </button>
                          </div>
                        </div>

                        <p style={{
                          fontSize: '13px',
                          color: '#666',
                          lineHeight: '1.5',
                          marginBottom: '8px',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {interaction.notes}
                        </p>

                        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#999' }}>
                          <span>
                            👤 {interaction.created_by_name || 'Usuário'}
                          </span>
                          <span>
                            📅 {formatDate(interaction.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'followup' ? (
        <LeadFollowUp
          leadId={leadId}
          leadName={leadName}
          followUpDate={followUpDate}
        />
      ) : activeTab === 'integrations' ? (
        <QuickIntegrations
          leadPhone={leadPhone}
          leadEmail={leadEmail}
          leadName={leadName}
        />
      ) : (
        <LeadDocuments
          leadId={leadId}
          leadName={leadName}
          onDocumentUploaded={() => {}}
        />
      )}

      {/* Footer */}
      <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '16px', textAlign: 'right' }}>
        <button
          onClick={onClose}
          style={{
            padding: '8px 16px',
            backgroundColor: '#757575',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Fechar
        </button>
      </div>
    </div>
  )
}

export default LeadInteractions
